import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserLoginRequestDto } from '../dtos/account/user-login-request.dto';
import { ForgetPasswordRequestDto } from '../dtos/forget-password-request.dto';
import { PasswordSettingsDto } from '../dtos/password-settings.dto';
import { ResetPasswordDto } from '../dtos/reset-password.dto';
import { SwitchRoleRequestDto } from '../dtos/switch-role-request.dto';
import { UserLogoutRequestDto } from '../dtos/user-logout-request.dto';
import { UserProfileDto } from '../dtos/user-profile.dto';
import { UserRegistrationRequestDto } from '../dtos/user-registeration.dto';
import { UserDto } from '../dtos/user.dto';
import { APP, Constants } from '../shared/constants/constants';
import { Cookies } from '../shared/constants/cookie.constants';
import { isDevelopment, isLocal } from '../utils/util-helper';
import { ClientContextService } from './client-context.service';
import { LanguageService } from './language.service';
import { NotificationSignalRService } from './signalr/notification-signal-r.service';
import { UserService } from './user.service';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl + '/api/account';

  private jwtHelper = new JwtHelperService(); // Create an instance of JwtHelperService

  private http = inject(HttpClient);
  private router = inject(Router);
  private notificationSignalRService = inject(NotificationSignalRService);
  languageService = inject(LanguageService);
  private userService = inject(UserService);
  private cookieService = inject(CookieService);
  private clientContextService = inject(ClientContextService);

  /**
   * AUTH STATE MODEL
   * -----------------
   * authState$  -> true/false authentication
   * authReady$  -> whether initialization is complete (CRITICAL)
   */
  private authStateSubject = new BehaviorSubject<boolean>(false);
  private authReadySubject = new BehaviorSubject<boolean>(false);

  /** Emits current authentication state */
  public authState$ = this.authStateSubject.asObservable();

  /** Emits when auth system has fully initialized */
  public authReady$ = this.authReadySubject.asObservable();

  private refreshInFlight$?: Observable<boolean>;

  constructor() {}

  // =========================================================
  // INITIALIZATION (CRITICAL FOR FIXING YOUR ISSUE)
  // =========================================================

  /**
   * Initializes authentication state on app startup.
   *
   * This ensures:
   * - No guard runs before auth is known
   * - Refresh token is validated once
   * - Token state is synchronized before routing begins
   *
   * MUST be called via APP_INITIALIZER
   */
  initAuth(): Observable<boolean> {
    const hasToken = this.cookieService.check(Cookies.AUTH_TOKEN);

    if (!hasToken) {
      this.setAuth(false);
      this.markReady();
      return of(false);
    }

    // Access token is still valid
    if (!this.isTokenExpired()) {
      this.setAuth(true);
      this.markReady();
      return of(true);
    }

    // Access token expired, verify refresh token
    return this.validateRefreshToken().pipe(
      tap((isValid) => {
        this.setAuth(isValid);
        this.markReady();
      }),
      catchError(() => {
        this.setAuth(false);
        this.markReady();
        return of(false);
      })
    );
  }

  /**
   * Marks authentication initialization as complete.
   *
   * Guards should wait for authReady$ before evaluating access.
   * This prevents race conditions during application startup,
   * page refreshes, and deep-link navigation.
   */
  private markReady(): void {
    this.authReadySubject.next(true);
  }

  /**
   * Centralized auth state update.
   */
  private setAuth(isAuthenticated: boolean): void {
    this.authStateSubject.next(isAuthenticated);
  }

  /**
   * Returns current synchronous authentication state.
   * IMPORTANT: Should only be used AFTER authReady$ emits true.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    return !this.jwtHelper.isTokenExpired(token);
  }

  /**
   * Validates refresh token with backend.
   */
  validateRefreshToken(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/is-refresh-token-valid`);
  }

  /**
   * Attempts to silently refresh the user's access token using the refresh token.
   *
   * This method is the SINGLE SOURCE OF TRUTH for token refresh.
   * It is used by:
   * - AuthGuard (session recovery)
   * - HTTP Interceptor (401 recovery)
   * - App initialization (restoring session)
   *
   * ------------------------------------------------------------
   * IMPORTANT DESIGN RULES:
   * - Only ONE refresh request can run at a time (single-flight)
   * - All callers share the same Observable (shareReplay)
   * - Refresh state is reset after completion (finalize)
   * ------------------------------------------------------------
   *
   * SUCCESS FLOW:
   * - Backend issues new access token (stored in cookies by backend or interceptor logic)
   * - Auth state is set to true
   * - SignalR is restarted to use new token
   *
   * FAILURE FLOW:
   * - Auth state is set to false
   * - Caller (guard/interceptor) decides navigation (usually login)
   *
   * @returns Observable<boolean>
   * true  → refresh succeeded
   * false → refresh failed (refresh token invalid/expired)
   */
  refreshToken(): Observable<boolean> {
    // SINGLE-FLIGHT GUARD:
    // If a refresh is already running, reuse it instead of creating a new HTTP call.
    // This prevents duplicate refresh calls from:
    // - AuthGuard
    // - HTTP interceptor
    // - concurrent API requests
    if (this.refreshInFlight$) {
      if (isLocal() || isDevelopment()) {
        console.log('[AuthService] Reusing existing refresh request');
      }
      return this.refreshInFlight$;
    }

    if (isLocal() || isDevelopment()) {
      console.log('[AuthService] Starting token refresh...');
    }

    this.refreshInFlight$ = this.http.post(`${this.apiUrl}/refresh`, {}).pipe(
      tap((response) => {
        if (isLocal() || isDevelopment()) {
          console.log('[AuthService] Refresh response:', response);
          console.log('[AuthService] Token refresh successful');
        }

        // IMPORTANT:
        // SignalR depends on JWT → must reconnect after refresh
        this.notificationSignalRService.startConnection();

        // Update auth state AFTER successful refresh
        this.setAuth(true);
      }),

      map(() => true),

      catchError((error: HttpErrorResponse) => {
        if (isLocal() || isDevelopment()) {
          console.warn('[AuthService] Refresh failed:', error);
        }

        // Mark user as unauthenticated
        this.setAuth(false);

        return of(false);
      }),

      finalize(() => {
        if (isLocal() || isDevelopment()) {
          console.log('[AuthService] Refresh stream completed (resetting cache)');
        }

        // Allow future refresh attempts
        this.refreshInFlight$ = undefined;
      }),

      // Ensures all subscribers share the same result
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  /**
   * Checks whether both access token (client-side) and refresh token (server-side)
   * are still valid.
   *
   * IMPORTANT:
   * ------------------------------------------------------------
   * This method is a *lightweight validation check* only.
   * It does NOT attempt to refresh tokens.
   * It does NOT modify authentication state.
   * ------------------------------------------------------------
   *
   * Use cases:
   * - App startup diagnostics
   * - Debugging session integrity issues
   * - Pre-auth validation checks (non-guard flows)
   *
   * Returns:
   * - true → both access token exists AND refresh token is valid
   * - false → session is definitely invalid
   */
  isAccessTokenAndRefreshTokenValid(): Observable<boolean> {
    const hasAccessToken = this.cookieService.check(Cookies.AUTH_TOKEN);

    if (isLocal() || isDevelopment()) {
      console.log('[AuthService] Access token exists:', hasAccessToken);
      console.log('[AuthService] Validating refresh token with backend...');
    }

    return this.validateRefreshToken().pipe(
      map((hasRefreshToken) => {
        const result = hasAccessToken && hasRefreshToken;

        if (isLocal() || isDevelopment()) {
          console.log('[AuthService] Refresh token valid:', hasRefreshToken);
          console.log('[AuthService] Full session valid:', result);
        }

        return result;
      })
    );
  }

  /**
   * Determines whether the current session is still usable.
   *
   * IMPORTANT DESIGN RULE:
   * ------------------------------------------------------------
   * This method is SAFE and SIDE-EFFECT FREE.
   * It MUST NOT trigger refresh logic.
   * It is intended for AuthGuard and routing decisions only.
   * ------------------------------------------------------------
   *
   * Logic:
   * 1. If access token exists → assume session is potentially valid
   * 2. If missing → check if refresh token is still valid
   *
   * This avoids unnecessary refresh calls in multiple layers
   * (guards + interceptors), preventing duplicate network calls.
   *
   * Returns:
   * - true → session is usable or can be recovered
   * - false → user must be redirected to login
   */
  isSessionValid(): Observable<boolean> {
    const hasAccessToken = this.cookieService.check(Cookies.AUTH_TOKEN) || this.isAuthenticated();

    if (isLocal() || isDevelopment()) {
      console.log('[AuthService] Session check → hasAccessToken:', hasAccessToken);
    }

    // If we already have an access token → session is considered valid
    if (hasAccessToken) {
      if (isLocal() || isDevelopment()) {
        console.log('[AuthService] Session valid via access token');
      }
      return of(true);
    }

    // No access token → ask backend if refresh token is still valid
    if (isLocal() || isDevelopment()) {
      console.log('[AuthService] No access token → validating refresh token');
    }

    return this.validateRefreshToken().pipe(
      map((isValid) => {
        if (isLocal() || isDevelopment()) {
          console.log('[AuthService] Refresh token valid:', isValid);
        }

        return isValid;
      })
    );
  }

  /**
   * Sends login request to the backend.
   *
   * IMPORTANT:
   * This method is intentionally "pure":
   * - It only performs authentication
   * - It does NOT mutate auth state
   * - It does NOT set cookies or start services
   *
   * All side effects must happen in:
   * → handleSuccessfulLogin()
   */
  login(email: string, password: string): Observable<UserDto> {
    const payload: UserLoginRequestDto = {
      email,
      password,
      clientContext: this.clientContextService.buildClientContext()
    };

    return this.http.post<UserDto>(`${this.apiUrl}/login`, payload);
  }

  /**
   * Performs all client-side initialization required after a successful login.
   *
   * This method exists to ensure authentication-related side effects occur
   * in a single location and complete before application navigation occurs.
   *
   * Responsibilities:
   * - Store authenticated user information
   * - Update authentication state
   * - Apply user preferences
   * - Start authenticated services (SignalR, etc.)
   *
   * Returns an Observable so callers can wait for initialization to finish
   * before continuing with navigation or additional application logic.
   */
  handleSuccessfulLogin(user: UserDto): Observable<void> {
    // Cache the authenticated user's profile for application-wide access.
    this.userService.setUser(user);

    // Notify the application that authentication has succeeded.
    // Components, guards, and services may be subscribed to authState$.
    this.setAuth(true);

    this.languageService.setLoggedInUserLanguage(user.preferredLanguage, this.getTokenExpirationDate());

    // Persist the user's preferred theme so it survives page refreshes.
    const theme = user.theme.isDark ? APP.DARK_THEME : APP.LIGHT_THEME;

    this.cookieService.set(Cookies.USER_THEME, theme, {
      path: '/',
      domain: environment.cookieDomain,
      secure: true,
      sameSite: 'Lax',
      expires: this.getTokenExpirationDate() ?? undefined
    });

    // SignalR connections require a valid access token.
    // Start (or restart) the connection after authentication succeeds.
    this.notificationSignalRService.startConnection();

    return of(void 0);
  }

  getUserOnboardingProfile(): Observable<UserProfileDto> {
    return this.http.get<UserProfileDto>(`${this.apiUrl}/${'onboarding/profile'}`);
  }

  switchRoles(switchRole: SwitchRoleRequestDto): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/${'switch-role'}`, switchRole);
  }

  setUserAfterLogin(user: UserDto): void {
    this.userService.setUser(user);
  }

  register(user: UserRegistrationRequestDto): Observable<UserRegistrationRequestDto> {
    return this.http.post<UserRegistrationRequestDto>(`${this.apiUrl}/${'registration'}`, user);
  }

  confirmRegistration(token: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${'confirm-registration'}`, { token });
  }

  forgetPassword(request: ForgetPasswordRequestDto) {
    return this.http.post<boolean>(`${this.apiUrl}/${'forget-password'}`, request);
  }

  resetPassword(request: ResetPasswordDto) {
    return this.http.post<boolean>(`${this.apiUrl}/${'reset-password'}`, request);
  }

  changePassword(passwordSettings: PasswordSettingsDto): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/${'change-password'}`, passwordSettings);
  }

  /**
   * Performs local logout cleanup regardless of whether
   * the server-side logout request succeeds or fails.
   */
  private performLogoutCleanup(): void {
    this.setAuth(false);

    this.notificationSignalRService.stopConnection();

    this.userService.setUser(null);
    this.userService.removeUser();

    this.deleteAuthToken();

    this.cookieService.delete(Cookies.USER_THEME, '/', environment.cookieDomain);
    this.cookieService.delete(Cookies.USER_LANG, '/', environment.cookieDomain);
    // Set the language back to the application language
    this.languageService.initializeApplicationLanguage();
  }

  /**
   * Logs the current user out.
   *
   * Flow:
   * 1. Attempt to notify the backend that the session is ending.
   * 2. Regardless of success or failure, clear all local authentication state.
   * 3. Stop authenticated services.
   * 4. Reset application preferences.
   * 5. Redirect the user to the login page.
   *
   * The user should never remain logged in locally because
   * of a failed logout API request.
   */
  logout(): void {
    console.log('Logging out');

    const user = this.userService.getUser();

    if (!user) {
      this.forceLogout();
      return;
    }

    const userLogout = new UserLogoutRequestDto(user.currentRoleId, user.sessionUid, this.cookieService.get(Cookies.AUTH_TOKEN));

    const hasToken = this.cookieService.check(Cookies.AUTH_TOKEN);
    const logoutRequest = hasToken
      ? this.http.post<UserLogoutRequestDto>(`${this.apiUrl}/logout`, userLogout)
      : this.http.post<UserLogoutRequestDto>(`${this.apiUrl}/logout/anonymous`, userLogout);

    logoutRequest
      .pipe(
        finalize(() => {
          this.performLogoutCleanup();
          this.router.navigate(['login']);
        })
      )
      .subscribe({
        next: () => {
          console.log('Logout completed successfully.');
        },
        error: (error) => {
          console.error('Logout request failed.', error);
        }
      });
  }

  forceLogout() {
    if (isLocal()) console.log('Force logout');

    this.performLogoutCleanup();
    this.router.navigate(['login']);
  }

  setToken(token: string) {
    this.cookieService.set(Constants.STORAGE_TOKEN, token, this.setCookiesOptions());
  }

  setCookiesOptions(isLogout: boolean = false) {
    let expiredDate: any;
    if (isLogout) {
      expiredDate = new Date(0).toUTCString();
    } else {
      expiredDate = new Date();
      expiredDate.setDate(expiredDate.getDate() + 20);
    }

    let options = {};
    options = {
      expires: expiredDate,
      domain: environment.cookieDomain,
      path: '/'
    };
    return options;
  }

  // ----------------------------------------------------- JWT helper methods

  getToken(): string {
    return this.cookieService.get(Cookies.AUTH_TOKEN);
  }

  /**
   *
   * @returns True if the auth token was deleted
   */
  deleteAuthToken(): boolean {
    console.log('deleteAuthToken()');
    this.cookieService.delete(Cookies.AUTH_TOKEN, '/', environment.cookieDomain);
    if (!this.cookieService.check(Cookies.AUTH_TOKEN)) {
      return true;
    }
    return false;
  }

  // Check if the token is expired
  isTokenExpired(): boolean {
    const token = this.getToken();
    return token ? this.jwtHelper.isTokenExpired(token) : true;
  }

  decodeToken(): any {
    const token = this.getToken();
    return token ? this.jwtHelper.decodeToken(token) : null;
  }

  getTokenExpirationDate(): Date | null {
    const token = this.getToken();
    return token ? this.jwtHelper.getTokenExpirationDate(token) : null;
  }

  // --------------------------------- Claims

  getClaimValue(key: string): string | null {
    const token = this.getToken();

    if (token) {
      const decodedToken = this.jwtHelper.decodeToken(token);

      if (decodedToken && decodedToken[key]) {
        return decodedToken[key];
      }
    }

    return null;
  }

  // --------------------------------------

  // #region User Info
  // get userInfo(): any {
  //   if (!this.cookieService.get(Constants.USER_INFO)) {
  //     return null;
  //   }
  //   return JSON.parse(this.cookieService.get(Constants.USER_INFO)!);
  // }

  // setUserInfo(user: any) {
  //   console.log("setUserInfo(user: any)")
  //   console.log(user)
  //   console.log(JSON.stringify(user))
  //   this.cookieService.set(Constants.USER_INFO, JSON.stringify(user), this.setCookiesOptions());
  // }
  //#endregion
}
